<template>
  <div class="serum-ingredients-tab">
    <div class="serum-ingredients-tab__common-description common-description">
      <p v-for="(paragraph, i) of serumDetails" :key="i" class="common-description__paragraph">
        {{ $t(paragraph) }}
      </p>
    </div>

    <div class="serum-ingredients-tab__ingredient-descriptions ingredient-descriptions">
      <div
        v-for="ingredient of ingredients"
        :key="getName(ingredient)"
        class="ingredient-descriptions__item ingredient"
      >
        <div
          class="ingredient__title ingredient-title"
          @click="$emit('ingredient-click', getName(ingredient))"
        >
          <serum-ingredient-color-box class="ingredient-title__color-box" :name="ingredient.name" />
          <span class="ingredient-title__text"
            >{{ getDisplayName(ingredient) }} {{ getConcentration(ingredient) }}%</span
          >
        </div>

        <p class="ingredient__description u-typography-helvetica u-text u-text--xs">
          {{ getDescription(ingredient) }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import SerumIngredientColorBox from '@/components/common/SerumIngredientColorBox.vue';

import { INGREDIENT_DESCRIPTION_KEY, INGREDIENT_NAME_KEY } from '@/constants/ingredients';
import { PRODUCT_NAMES, SERUM_USAGES } from '@/constants/treatment';
import { getIngredientName, getIngredientTotalConcentration } from '@/models/core/Ingredient';
import { getPackItemIngredients, getPackItemName, getPackItemUsage } from '@/models/core/PackItem';

const FACE_SERUM_DETAILS = {
  [SERUM_USAGES.MORNING_ONLY]: ['section.yourTreatment.faceSerum.details.morningSerum.p1'],
  [SERUM_USAGES.EVENING_ONLY]: ['section.yourTreatment.faceSerum.details.eveningSerum.p1'],
  [SERUM_USAGES.MORNING_AND_EVENING]: [
    'section.yourTreatment.faceSerum.details.duplicatedMorningAndEveningSerum.p1'
  ]
};

const EYE_CONTOUR_SERUM_DETAILS = {
  [SERUM_USAGES.MORNING_ONLY]: [
    // withFaceSerum and withoutFaceSerum are the same texts, don't pay attention to the name
    'section.yourTreatment.eyeContourSerum.descriptionText.morningSerum'
  ],
  [SERUM_USAGES.EVENING_ONLY]: [
    'section.yourTreatment.eyeContourSerum.descriptionText.eveningSerum'
  ],
  [SERUM_USAGES.MORNING_AND_EVENING]: [
    'section.yourTreatment.eyeContourSerum.descriptionText.morningAndEveningSerum'
  ]
};

export default {
  name: 'SerumIngredientsTab',
  components: { SerumIngredientColorBox },
  props: {
    packItem: {
      type: Object,
      required: true
    },
    packUsage: {
      type: String,
      required: true
    }
  },
  computed: {
    productName() {
      return getPackItemName(this.packItem);
    },
    formulaUsage() {
      return getPackItemUsage(this.packItem);
    },
    ingredients() {
      return getPackItemIngredients(this.packItem);
    },
    serumDetails() {
      if (this.productName === PRODUCT_NAMES.FACE_SERUM) {
        return FACE_SERUM_DETAILS[this.formulaUsage];
      }

      // TODO: we may need separate mapping for generic eye serum

      return EYE_CONTOUR_SERUM_DETAILS[this.formulaUsage];
    }
  },
  methods: {
    getName(ingredient) {
      return getIngredientName(ingredient);
    },
    getDescription(ingredient) {
      const name = getIngredientName(ingredient);

      return this.$t(INGREDIENT_DESCRIPTION_KEY[name]);
    },
    getDisplayName(ingredient) {
      const name = getIngredientName(ingredient);

      return this.$t(INGREDIENT_NAME_KEY[name]);
    },
    getConcentration(ingredient) {
      return getIngredientTotalConcentration(ingredient).toFixed(1);
    }
  }
};
</script>

<style lang="scss" scoped>
.serum-ingredients-tab {
  font-family: var(--u-font-family-Helvetica);

  &__common-description {
    font-size: 18px;
    line-height: 27px;
    margin-bottom: 32px;
  }
}
.common-description {
  &__paragraph:not(:last-child) {
    margin-bottom: 8px;
  }
}

.ingredient-descriptions {
  &__item {
    max-width: 260px;
  }

  &__item:not(:last-child) {
    margin-bottom: 24px;
  }
}

.ingredient-title {
  cursor: pointer;
  display: flex;
  align-items: center;

  &__color-box {
    margin-inline-end: 12px;
  }

  &__text {
    font-size: 18px;
    line-height: 27px;
    letter-spacing: 0.4px;
    text-decoration-line: underline;
    color: var(--u-color-grey-900);
  }
}

.ingredient {
  &__title {
    margin-bottom: 8px;
  }

  &__description {
    color: var(--u-color-grey-700);
  }
}

@media (min-width: 960px) {
  .ingredient-descriptions {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;

    &__item:not(:last-child) {
      margin-bottom: 32px;
    }
  }
}
</style>
